<template>
  <div
    class="w-2/3 mx-auto"
    :class="{ 'load load--fill': isLoading }"
  >
    <form
      method="POST"
      class="mt-8"
      @submit.prevent="save"
    >
      <fieldset class="mb-8">
        <legend
          class="fieldset-legend"
          v-text="'Set Notification Settings:'"
        />
        <small
          v-if="formDisabled"
          v-text="'Since you are acting as a Super-Admin this form is disabled'"
        />
        <div
          v-can="'edit-profiles'"
          class="field-list pt-3"
        >
          <div class="field sm:w-1/2">
            <input
              id="receive_profile_updates_via_email"
              v-model="form.receive_profile_updates_via_email"
              type="checkbox"
              name="receive_profile_updates_via_email"
              :disabled="formDisabled"
              :aria-disabled="formDisabled"
            >
            <label
              for="receive_profile_updates_via_email"
              v-text="'Notify me via email when a member updates their profile.'"
            />
          </div>
        </div>
        <div
          v-can="'edit-profiles'"
          class="field-list pt-3"
        >
          <div class="field sm:w-1/2">
            <input
              id="receive_access_requests_via_email"
              v-model="form.receive_access_requests_via_email"
              type="checkbox"
              name="receive_access_requests_via_email"
              :disabled="formDisabled"
              :aria-disabled="formDisabled"
            >
            <label
              for="receive_access_requests_via_email"
              v-text="'Notify me via email when someone requests access to join.'"
            />
          </div>
        </div>
        <div
          v-can="'edit-profiles'"
          class="field-list pt-3"
        >
          <div class="field sm:w-1/2">
            <input
              id="receive_form_submissions_via_email"
              v-model="form.receive_form_submissions_via_email"
              type="checkbox"
              name="receive_form_submissions_via_email"
              :disabled="formDisabled"
              :aria-disabled="formDisabled"
            >
            <label
              for="receive_form_submissions_via_email"
              v-text="'Notify me via email when someone submits a form.'"
            />
          </div>
        </div>
        <div
          v-if="groupAdminsEnabled && canAdminGroups"
          class="field-list pt-3"
        >
          <div class="field sm:w-1/2">
            <input
              id="receive_member_join_group_via_email"
              v-model="form.receive_member_join_group_via_email"
              type="checkbox"
              name="receive_member_join_group_via_email"
              :disabled="formDisabled"
              :aria-disabled="formDisabled"
            >
            <label
              for="receive_member_join_group_via_email"
              v-text="'Notify me in the app when a member joins a group.'"
            />
          </div>
        </div>
        <div
          v-if="groupAdminsEnabled && canAdminGroups"
          class="field-list pt-3"
        >
          <div class="field sm:w-1/2">
            <input
              id="receive_member_join_group_via_app"
              v-model="form.receive_member_join_group_via_app"
              type="checkbox"
              name="receive_member_join_group_via_app"
              :disabled="formDisabled"
              :aria-disabled="formDisabled"
            >
            <label
              for="receive_member_join_group_via_app"
              v-text="'Notify me via email when a member joins a group.'"
            />
          </div>
        </div>
      </fieldset>
      <div class="flex justify-around py-4">
        <button
          type="submit"
          class="btn"
          :class="{ load: isLoading, 'btn--disabled': formDisabled }"
          v-text="saveButtonText"
        />
      </div>
    </form>
  </div>
</template>

<script type="text/babel">
export default {
  name: 'NotificationSettings',
  props: {
    membershipUuid: {
      type: String,
      required: false,
      default: '',
    },
    canAdminGroups: {
      type: Boolean,
      required: true,
    },
    groupAdminsEnabled: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      membership: {},
      form: {
        receive_profile_updates_via_email: false,
        receive_access_requests_via_email: false,
        receive_form_submissions_via_email: false,
        receive_member_join_group_via_email: false,
        receive_member_join_group_via_app: false,
      },
    }
  },
  computed: {
    saveButtonText() {
      return this.isLoading ? 'Saving...' : 'Save Notification Settings'
    },
    settings() {
      return this.membership.attributes.settings
    },
    formDisabled() {
      return !this.membershipUuid
    },
  },
  mounted() {
    this.fetch()
  },
  methods: {
    fetch() {
      if (this.membershipUuid) {
        this.isLoading = true

        this.$api.active().get(`memberships/${this.membershipUuid}`).then((response) => {
          this.membership = response.data

          this.parseMemberSettings()

          this.isLoading = false
        }).catch((errors) => {
          if (_.get(errors.response, 'status') === 422) {
            const vm = this

            _.forEach(errors.response.data.errors, function (error) {
              vm.$store.commit('baseAddNotification', {
                title: 'Error!',
                text: _.first(error),
                type: 'error',
                timeout: false,
              })
            })
          } else {
            this.$store.commit('baseAddNotification', {
              title: 'Error!',
              text: errors.response.data.message,
              type: 'error',
              timeout: false,
            })
          }

          this.isLoading = false
        })
      }
    },
    save() {
      this.$api.public().put(`me/memberships/${this.membershipUuid}/settings`, this.form).then((response) => {
        if (response.data.status === 'failed') {
          this.$store.commit('baseAddNotification', {
            title: 'Error!',
            text: response.data.message,
            type: 'error',
            timeout: false,
          })
        } else {
          this.$store.commit('baseAddNotification', {
            title: 'Success!',
            text: response.data.message,
            type: 'success',
          })

          this.$emit('NotificationSettingsUpdated')
        }

        this.isLoading = false
      }).catch((errors) => {
        if (_.get(errors.response, 'status') === 422) {
          const vm = this

          _.forEach(errors.response.data.errors, function (error) {
            vm.$store.commit('baseAddNotification', {
              title: 'Error!',
              text: _.first(error),
              type: 'error',
              timeout: false,
            })
          })
        } else {
          this.$store.commit('baseAddNotification', {
            title: 'Error!',
            text: errors.response.data.message,
            type: 'error',
            timeout: false,
          })
        }

        this.isLoading = false
      })
    },
    parseMemberSettings() {
      if (this.settings) {
        Object.keys(this.form).map((key) => {
          if (key in this.settings) {
            this.form[key] = this.settings[key]
          }
        })
      }
    },
  },
}
</script>
